import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

interface Props {
  id: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  onAutoAdvance: () => void;
}

export function SingleChoiceQuestion({ id, options, value, onChange, onAutoAdvance }: Props) {
  const [animating, setAnimating] = useState<string | null>(null);

  // Ref to always call the latest onAutoAdvance — fixes stale closure where the
  // captured handleNext sees the pre-selection `isCurrentValid=false` snapshot.
  const onAutoAdvanceRef = useRef(onAutoAdvance);
  useEffect(() => {
    onAutoAdvanceRef.current = onAutoAdvance;
  });

  const handleSelect = (option: string) => {
    if (animating) return;

    setAnimating(option);
    onChange(option);

    setTimeout(() => {
      setAnimating(null);
      onAutoAdvanceRef.current();
    }, 400);
  };

  return (
    <div role="radiogroup" aria-label={id} className="flex flex-col gap-3">
      {options.map(option => {
        const isSelected = value === option;
        const isAnimating = animating === option;

        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => handleSelect(option)}
            className={`
              flex items-center gap-3 w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200
              ${isSelected
                ? 'border-hit-gold bg-red-50'
                : 'border-hit-gray-light bg-white hover:border-hit-gold/50 hover:bg-red-50/30'
              }
            `}
          >
            <span
              className={`
                flex-shrink-0 w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-all duration-300
                ${isSelected ? 'bg-hit-gold border-hit-gold' : 'border-hit-gray-mid bg-white'}
              `}
              aria-hidden="true"
            >
              {isSelected && (
                <motion.svg
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: isAnimating ? 1.2 : 1, opacity: 1 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  width="12"
                  height="10"
                  viewBox="0 0 12 10"
                  fill="none"
                >
                  <path
                    d="M1 5L4.5 8.5L11 1.5"
                    stroke="#FFFFFF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </motion.svg>
              )}
            </span>
            <span className="text-sm font-medium text-hit-black">{option}</span>
          </button>
        );
      })}
    </div>
  );
}
